import { ComponentType, lazy, Suspense } from "react";
import { RouteObject } from "react-router-dom";
import { CircularProgress, Stack } from "@mui/material";

import { ADMIN_NOT_FOUND_SX } from "@/apps/admin/components/Layout/notFoundSx";
import { RouteErrorPage } from "@/common/components/RouteErrorPage/RouteErrorPage";

/** Admin's route error: verbose (AD1), in the page area below the top bar. */
export const ADMIN_ROUTE_ERROR = (
  <RouteErrorPage verbose notFoundSx={ADMIN_NOT_FOUND_SX} />
);

/** What every admin page shows while its chunk downloads: the page area, a spinner in the middle. */
export const PageLoading = () => (
  <Stack
    alignItems="center"
    justifyContent="center"
    sx={ADMIN_NOT_FOUND_SX}
    data-testid="adminPageLoading"
  >
    <CircularProgress aria-label="Loading page" />
  </Stack>
);

/**
 * A route's `element` and `errorElement` for a page loaded on first visit (ARCHITECTURE A7, item
 * 1). The page renders inside GlobalLayout's Outlet, so the shell stays while it loads (Suspense)
 * and when it fails: a chunk that cannot be downloaded or a page that throws reaches this route's
 * `errorElement`, not the root's.
 */
export const lazyPage = (
  load: () => Promise<{ default: ComponentType }>
): Pick<RouteObject, "element" | "errorElement"> => {
  const Page = lazy(load);
  return {
    element: (
      <Suspense fallback={<PageLoading />}>
        <Page />
      </Suspense>
    ),
    errorElement: ADMIN_ROUTE_ERROR,
  };
};
